import { z } from "zod";
// Tên hiển thị của Apple đi CHUNG trần với PATCH /me — một luật, một hằng (users-me.ts chỉ
// import zod nên không có vòng lặp import).
import { FULL_NAME_MAX_LENGTH } from "./users-me.js";

/**
 * Trần độ dài cho MỌI credential social nhận từ client (US-E25.F1-0384 · review M5).
 *
 * Không có trần thì `min(1)` chấp nhận một chuỗi dài tuỳ ý, và với Google ta **ghép thẳng nó vào
 * URL** (`tokeninfo?id_token=…`) ⇒ tự tay dựng một request khổng lồ gửi ra ngoài. Đây là biên hệ
 * thống: chặn ở contract thì 0 byte chạm tầng dưới.
 *
 * 8 KiB là rộng rãi có chủ ý — ID token Google/Apple thực tế ~1–2 KB. Trần này chặn input phình,
 * KHÔNG phải để kiểm định dạng token (việc đó của provider).
 *
 * 🔴 Áp cho **cả bốn** provider, không riêng Google: gác một cửa rồi để ba cửa kia mở chính là
 * mẫu hình "guard canh nhầm chỗ" mà repo đã trả giá nhiều lần.
 */
export const SOCIAL_CREDENTIAL_MAX_LENGTH = 8192;

/**
 * Shared DTO cho đăng nhập/đăng ký bằng Zalo OAuth (US-E2.F1-0008 / @UC0001).
 * Một nguồn sự thật cho api (S01 — validate biên) và mobile (S02 — gọi + map lỗi).
 *
 * PKCE backend-side exchange (Lead 25/06): mobile lấy `authorization code` + `code_verifier`
 * qua SDK Zalo và gửi lên backend; BACKEND đổi code→access token với Zalo (app secret
 * chỉ ở server). Client KHÔNG bao giờ gửi access token Zalo lên — chỉ gửi code + verifier.
 */
export const zaloOAuthRequestSchema = z.object({
  /** Authorization code Zalo trả cho client sau khi user đồng ý. */
  code: z.string().min(1).max(SOCIAL_CREDENTIAL_MAX_LENGTH),
  /** PKCE code_verifier (chuỗi gốc; backend gửi kèm khi đổi code→token). */
  codeVerifier: z.string().min(1).max(SOCIAL_CREDENTIAL_MAX_LENGTH),
  /**
   * Redirect URI đã đăng ký với Zalo; phải khớp giá trị dùng lúc lấy code.
   *
   * 🔴 MEDIUM-2 review vòng 2: `.url()` **không** giới hạn độ dài — đo được
   * `'https://a.example/cb?x=' + 'y'.repeat(100000)` ⇒ `success: true` (100.023 ký tự). Và giá trị
   * này đi tiếp vào **log** (`zalo-oauth.client.ts` in nguyên văn khi lệch allowlist) lẫn **body
   * outbound** tới Zalo khi `ZALO_OAUTH_REDIRECT_URI` chưa cấu hình. Test cũ tưởng đã phủ nhưng chỉ
   * bắn `"x".repeat(8193)` — chuỗi đó rớt vì **không phải URL**, không phải vì độ dài ⇒ assertion
   * rỗng nghĩa cho đúng field này. Bài học: *một field rớt không có nghĩa nó rớt vì lý do ta nghĩ.*
   */
  redirectUri: z.string().url().max(SOCIAL_CREDENTIAL_MAX_LENGTH),
});

export type ZaloOAuthRequest = z.infer<typeof zaloOAuthRequestSchema>;

/**
 * Shared DTO đăng nhập/đăng ký bằng Google (US-E13.F2-0108-S16 / DR-04).
 *
 * Mobile lấy Google **ID token** (JWT ký bởi Google) qua SDK `google_sign_in` rồi
 * gửi lên backend. BACKEND verify chữ ký + audience (GOOGLE_ALLOWED_AUDIENCES) —
 * KHÔNG tin danh tính client tự khai. Client CHỈ gửi idToken (không secret).
 */
export const googleOAuthRequestSchema = z
  .object({
    /** Google ID token (JWT) client nhận từ SDK sau khi user đồng ý. */
    idToken: z.string().min(1).max(SOCIAL_CREDENTIAL_MAX_LENGTH),
  })
  // 🔴 US-E26.F3-0392-S01 (@UC9674) — `.strict()`, cùng lý lẽ đã áp cho Apple (`:124`):
  // `sub`/`email` là thứ CHỈ được đọc ra từ token ĐÃ VERIFY. Không `.strict()` thì zod **âm thầm
  // bỏ qua** field lạ ⇒ khe cho client tự khai danh tính tồn tại mà không ai thấy.
  // Đã rà client TRƯỚC khi siết (đổi hành vi đối ngoại): `apps/mobile`
  // (`google_oauth_request_dto.dart:13`) và `apps/flutter-web` (`auth_repository_impl.dart:60-66`)
  // đều gửi ĐÚNG một field ⇒ 0 regress. Siết bây giờ rẻ vì app chưa phát hành.
  .strict();

export type GoogleOAuthRequest = z.infer<typeof googleOAuthRequestSchema>;

/**
 * Shared DTO đăng nhập/đăng ký bằng Facebook (US-E13.F2-0108-S16 / DR-04).
 *
 * Mobile lấy Facebook **access token** qua SDK `flutter_facebook_auth` rồi gửi
 * lên backend. BACKEND verify token qua Graph `debug_token` với app-token
 * (FACEBOOK_APP_ID|FACEBOOK_APP_SECRET) — KHÔNG tin client tự khai. Client CHỈ
 * gửi accessToken (secret ở server).
 */
export const facebookOAuthRequestSchema = z
  .object({
    /** Facebook user access token client nhận từ SDK. */
    accessToken: z.string().min(1).max(SOCIAL_CREDENTIAL_MAX_LENGTH),
  })
  // 🔴 US-E26.F3-0392-S01 (@UC9674) — xem lý lẽ ở `googleOAuthRequestSchema`. Client đã rà:
  // `facebook_oauth_request_dto.dart:17` và `auth_repository_impl.dart:69-75` gửi đúng một field.
  .strict();

export type FacebookOAuthRequest = z.infer<typeof facebookOAuthRequestSchema>;

/**
 * Shared DTO đăng nhập/đăng ký bằng Apple (US-E23.F6-0336-S02 / @UC9546).
 *
 * Client lấy **identityToken** (JWT do Apple ký RS256) qua `sign_in_with_apple` rồi gửi lên.
 * BACKEND verify chữ ký bằng JWKS CÔNG KHAI của Apple + `aud` thuộc APPLE_ALLOWED_AUDIENCES —
 * KHÔNG tin client tự khai.
 *
 * 🔴 `.strict()` ở đây không phải cho đẹp: `sub`/`email` là thứ CHỈ được đọc ra từ token đã
 * verify. Nếu body nhận thêm được hai field đó thì client tự khai được mình là ai — nên gửi
 * field lạ phải 400 ngay ở biên, 0 dòng chạm DB.
 *
 * 🔴 `authorizationCode` CỐ Ý KHÔNG CÓ ở đây. Nó chỉ dùng để đổi lấy refresh token của Apple,
 * mà đường đó cần khoá riêng `.p8` (Service ID · Key ID · Team ID) — hiện chưa có, task S01 vẫn
 * khoá. Nhận một field không ai đọc chính là "toggle no-op" mà repo cấm: nó khiến người đọc
 * tưởng đường thu hồi token đã chạy. Khi nào có khoá thì thêm field CÙNG LÚC với consumer thật.
 */
export const appleOAuthRequestSchema = z
  .object({
    /** Apple identity token (JWT RS256) client nhận từ SDK sau khi user đồng ý. */
    identityToken: z.string().min(1).max(SOCIAL_CREDENTIAL_MAX_LENGTH),
    /**
     * Tên hiển thị — Apple CHỈ trả ở lần đăng nhập ĐẦU TIÊN và **không nằm trong token**,
     * nên đây là dữ liệu client tự khai. Chỉ dùng làm tên hiển thị lúc TẠO user; không bao
     * giờ là danh tính, và không ghi đè tên của user đã có. Cùng trần với PATCH /me.
     */
    fullName: z.string().trim().min(1).max(FULL_NAME_MAX_LENGTH).optional(),
    /**
     * 🔴 **Nonce THÔ do client sinh — BẮT BUỘC.** Chống REPLAY.
     *
     * `identityToken` là bearer credential sống ~10 phút, và đường Android (S05) **đặt nó vào
     * một URL** (`intent://…?id_token=…`) ⇒ nó nằm trong lịch sử Chrome Custom Tab của thiết bị
     * và trong access-log của bất kỳ proxy nào ghi response header. `aud` chặn token của app
     * KHÁC, `exp` giới hạn cửa sổ — nhưng **không có gì chặn chính token đó bị dùng lại**.
     * Nonce là control Apple thiết kế đúng cho việc này.
     *
     * Giao kèo: client sinh chuỗi ngẫu nhiên, gửi **`sha256(nonce)`** cho Apple ở bước authorize,
     * rồi gửi **nonce THÔ** lên đây; server băm lại và so với claim `nonce` trong token.
     * ⇒ Kẻ nhặt được token trong URL **không có** nonce thô ⇒ replay thất bại.
     *
     * 🔴 **BẮT BUỘC, không `.optional()`** — cố ý fail-closed. Cho phép vắng nonce là để lại
     * đúng một đường vòng mà kẻ tấn công sẽ chọn. Client chưa phát hành nên đây là **cửa sổ rẻ
     * nhất** để bắt buộc; sau khi app lên máy người dùng thì siết lại là breaking change.
     * (Phát hiện ở review đối kháng PR #375; không nằm trong AC nào của S01–S05.)
     */
    nonce: z.string().min(1).max(256),
  })
  .strict();

export type AppleOAuthRequest = z.infer<typeof appleOAuthRequestSchema>;

/**
 * Response thành công của social auth. C3: KHÔNG chứa access token provider, secret, hay
 * refreshTokenHash. `isNewUser` cho client biết lần đầu (→ onboarding) hay đã có (→ app).
 * Dùng chung cho Zalo/Google/Facebook (shape identical).
 */
export interface ZaloOAuthResponse {
  accessToken: string;
  refreshToken: string;
  isNewUser: boolean;
}

/** Alias rõ nghĩa: response social auth chung (mọi provider trả cùng shape). */
export type SocialOAuthResponse = ZaloOAuthResponse;

/**
 * Hằng message/code lỗi social auth dùng chung (DRY api ↔ mobile).
 * KHÔNG tiết lộ chi tiết lỗi provider/secret — message chung an toàn cho UI.
 *
 * Message mặc định giữ text Zalo cũ (backward-compat các test/mobile đang gắn).
 * Per-provider message giúp UI báo đúng nút nào lỗi.
 */
export const SOCIAL_VERIFY_FAILED_MESSAGE = "Đăng nhập bằng Zalo thất bại. Thử lại.";
export const GOOGLE_VERIFY_FAILED_MESSAGE = "Đăng nhập bằng Google thất bại. Thử lại.";
export const FACEBOOK_VERIFY_FAILED_MESSAGE = "Đăng nhập bằng Facebook thất bại. Thử lại.";
export const APPLE_VERIFY_FAILED_MESSAGE = "Đăng nhập bằng Apple thất bại. Thử lại.";

/** Machine code ổn định, gắn vào RFC7807 problem+json `code` (S01 AC3). */
export const SOCIAL_VERIFY_FAILED_CODE = "SOCIAL_VERIFY_FAILED";

/**
 * 🔴 US-E25.F1-0384-S03 (@UC0002) — **503: ta KHÔNG KẾT LUẬN ĐƯỢC**, khác hẳn 401 "kết luận là sai".
 *
 * Trước đây bốn nguyên nhân khác hẳn nhau cùng ra 401: token sai · `aud` lệch · **provider timeout**
 * · **provider 5xx / mất mạng**. Hai cái sau là **sự cố hạ tầng bị trình bày thành lỗi người dùng**:
 * họ thấy "đăng nhập thất bại", thử lại 5 lần rồi kết luận app hỏng — trong khi token của họ hoàn
 * toàn hợp lệ. Cùng họ với luật đã có: thiếu tỷ giá ⇒ **422 `FX_RATE_MISSING`**, không bịa và không
 * đổ lỗi lung tung.
 *
 * 🔴 FAIL-CLOSED vẫn là mặc định: **không phân loại được ⇒ 401**, không phải 503. Nói "cứ thử lại
 * đi" với một token thật sự sai là dạy người dùng thử vô ích.
 *
 * QĐ người dùng 12/08/2026: áp cho CẢ Google/Facebook/Apple/Zalo — một hợp đồng lỗi, không phải
 * mỗi provider một kiểu.
 */
export const SOCIAL_VERIFY_UNAVAILABLE_CODE = "SOCIAL_VERIFY_UNAVAILABLE";

/**
 * 🔴 **Câu này KHÔNG bao giờ tới client qua `detail` — client PHẢI map từ `code`.**
 *
 * `ProblemDetailFilter` che `detail` của **mọi** 5xx thành *"Đã xảy ra lỗi hệ thống…"* (cố ý: 5xx
 * hay mang nội dung nội bộ). 503 là 5xx ⇒ câu dưới đây bị che. Khác hẳn `*_VERIFY_FAILED_MESSAGE`
 * (401 < 500 nên đi qua `detail` được) — đừng suy từ anh em của nó.
 *
 * ⇒ Hằng này là **mục catalog phía CLIENT** (`code` → câu chữ), đúng quy ước repo *"`code` là mã ổn
 * định, `detail` chỉ để debug, KHÔNG phải nguồn hiển thị"*. Nó không phải hằng chết, nhưng cũng
 * **không phải** thứ server gửi đi.
 *
 * 🔴 HIGH-4 review vòng 2 — VÌ SAO CÂU NÀY KHÔNG HỨA "thử lại sau ít phút" NỮA:
 * 503 gộp hai thứ khác hẳn nhau về thời gian sống. `429`/`5xx` là TẠM THỜI, chờ là hết. Nhưng
 * `403` (Google `accessNotConfigured`, Meta app còn Development mode) là **VĨNH VIỄN** cho tới khi
 * con người đổi cấu hình. Hứa "vài phút" cho ca thứ hai chính là phản-mẫu mà task này sinh ra để
 * xoá — chỉ đổi chủ ngữ: thay vì dạy người dùng thử vô ích với một token sai, ta dạy họ thử vô ích
 * với một sự cố không bao giờ tự khỏi.
 * ⇒ Câu chữ nay ĐÚNG cho cả hai ca: nói rõ **lỗi thuộc về ta**, không hứa mốc thời gian nào.
 * Việc phân biệt tạm-thời ↔ vĩnh-viễn nằm ở `reason` trong log, phục vụ NGƯỜI TRỰC chứ không phải
 * người dùng cuối — họ không làm gì được với thông tin đó.
 *
 * 🔴 Đừng "sửa" bằng cách cho `detail` của 5xx lọt ra — đó là mở lại đúng cái cửa filter vừa đóng.
 * Hành vi trên dây đã bị khoá bằng test (`auth-google.int-spec.ts` @UC0002 AC6).
 */
export const SOCIAL_VERIFY_UNAVAILABLE_MESSAGE =
  "Không kết nối được với nhà cung cấp đăng nhập. Đây là sự cố phía chúng tôi, không phải tài khoản của bạn.";
